import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MapPinIcon, StarIcon } from 'lucide-react';
interface PopularHotelsProps {
  onNavigate?: (page: string) => void;
}
export function PopularHotels({
  onNavigate
}: PopularHotelsProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        delay: i * 0.15,
        ease: [0.22, 1, 0.36, 1]
      }
    })
  };
  return <section id="hotels" className="w-full bg-white py-24 sm:py-32" ref={ref} data-id="element-258">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-259">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-260">
          <motion.div initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {}} transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex items-center justify-center gap-3 mb-6" data-id="element-261">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-262">◆</span>
            <motion.div initial={{
            scaleX: 0
          }} animate={isInView ? {
            scaleX: 1
          } : {}} transition={{
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
          }} className="w-16 h-0.5 bg-[#D4A853] origin-center" data-id="element-263" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-264">◆</span>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" data-id="element-265">
            Popular Hotels to Stay
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-slate-500 text-lg max-w-xl mx-auto font-light" data-id="element-266">
            Handpicked luxury accommodations across Nepal's most stunning
            destinations
          </motion.p>
        </div>

        {/* Hotel Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-267">
          {/* Hotel 1 — Dwarika's Hotel */}
          <motion.article custom={0} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-xl transition-shadow duration-500 cursor-pointer" onClick={() => onNavigate?.('dwarika-hotel')} data-id="element-268">
            <div className="h-1 bg-gradient-to-r from-transparent via-[#D4A853] to-transparent" data-id="element-269" />
            <div className="relative overflow-hidden aspect-[3/2]" data-id="element-270">
              <img src="https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80" alt="Dwarika's Hotel luxury heritage property in Kathmandu" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" data-id="element-271" />
            </div>
            <div className="p-6" data-id="element-272">
              <div className="inline-flex items-center gap-1 bg-[#FAF8F5] rounded-lg p-1 px-2 mb-3" data-id="element-273">
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-274" />
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-275" />
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-276" />
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-277" />
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-278" />
              </div>
              <h3 className="font-playfair text-xl font-semibold text-slate-900 mb-2" data-id="element-279">
                Dwarika's Hotel
              </h3>
              <div className="flex items-center gap-1.5 text-slate-500 text-sm mb-4" data-id="element-280">
                <MapPinIcon className="w-4 h-4" data-id="element-281" />
                <span data-id="element-282">Kathmandu, Nepal</span>
              </div>
              <div className="flex items-center justify-between" data-id="element-283">
                <div data-id="element-284">
                  <span className="text-teal-600 text-lg font-semibold" data-id="element-285">
                    $350
                  </span>
                  <span className="text-slate-400 text-sm" data-id="element-286"> / night</span>
                </div>
                <button onClick={e => {
                e.stopPropagation();
                onNavigate?.('dwarika-hotel');
              }} className="inline-flex items-center px-5 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-colors duration-300 shadow-lg shadow-teal-600/20" data-id="element-287">
                  View Details
                </button>
              </div>
            </div>
          </motion.article>

          {/* Hotel 2 — Tiger Palace Resort */}
          <motion.article custom={1} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-xl transition-shadow duration-500" data-id="element-288">
            <div className="h-1 bg-gradient-to-r from-transparent via-[#D4A853] to-transparent" data-id="element-289" />
            <div className="relative overflow-hidden aspect-[3/2]" data-id="element-290">
              <img src="https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&q=80" alt="Tiger Palace Resort luxury property in Chitwan" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" data-id="element-291" />
            </div>
            <div className="p-6" data-id="element-292">
              <div className="inline-flex items-center gap-1 bg-[#FAF8F5] rounded-lg p-1 px-2 mb-3" data-id="element-293">
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-294" />
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-295" />
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-296" />
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-297" />
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-298" />
              </div>
              <h3 className="font-playfair text-xl font-semibold text-slate-900 mb-2" data-id="element-299">
                Tiger Palace Resort
              </h3>
              <div className="flex items-center gap-1.5 text-slate-500 text-sm mb-4" data-id="element-300">
                <MapPinIcon className="w-4 h-4" data-id="element-301" />
                <span data-id="element-302">Chitwan, Nepal</span>
              </div>
              <div className="flex items-center justify-between" data-id="element-303">
                <div data-id="element-304">
                  <span className="text-teal-600 text-lg font-semibold" data-id="element-305">
                    $280
                  </span>
                  <span className="text-slate-400 text-sm" data-id="element-306"> / night</span>
                </div>
                <a href="#" className="inline-flex items-center px-5 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-colors duration-300 shadow-lg shadow-teal-600/20" data-id="element-307">
                  Book Now
                </a>
              </div>
            </div>
          </motion.article>

          {/* Hotel 3 — Pavilions Himalayas */}
          <motion.article custom={2} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-xl transition-shadow duration-500" data-id="element-308">
            <div className="h-1 bg-gradient-to-r from-transparent via-[#D4A853] to-transparent" data-id="element-309" />
            <div className="relative overflow-hidden aspect-[3/2]" data-id="element-310">
              <img src="https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&q=80" alt="Pavilions Himalayas luxury eco-resort in Pokhara" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" data-id="element-311" />
            </div>
            <div className="p-6" data-id="element-312">
              <div className="inline-flex items-center gap-1 bg-[#FAF8F5] rounded-lg p-1 px-2 mb-3" data-id="element-313">
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-314" />
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-315" />
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-316" />
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-317" />
                <StarIcon className="w-4 h-4 fill-[#D4A853] text-[#D4A853]" data-id="element-318" />
              </div>
              <h3 className="font-playfair text-xl font-semibold text-slate-900 mb-2" data-id="element-319">
                Pavilions Himalayas
              </h3>
              <div className="flex items-center gap-1.5 text-slate-500 text-sm mb-4" data-id="element-320">
                <MapPinIcon className="w-4 h-4" data-id="element-321" />
                <span data-id="element-322">Pokhara, Nepal</span>
              </div>
              <div className="flex items-center justify-between" data-id="element-323">
                <div data-id="element-324">
                  <span className="text-teal-600 text-lg font-semibold" data-id="element-325">
                    $420
                  </span>
                  <span className="text-slate-400 text-sm" data-id="element-326"> / night</span>
                </div>
                <a href="#" className="inline-flex items-center px-5 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-full hover:bg-teal-700 transition-colors duration-300 shadow-lg shadow-teal-600/20" data-id="element-327">
                  Book Now
                </a>
              </div>
            </div>
          </motion.article>
        </div>
      </div>
    </section>;
}